/**
 * Payee naming suggestions (TMMAPP-0032 / TMMAPI-0056) — free-name status, the sole AI
 * `run` trigger, accept / reject / accept-all, and a suggestion's lines (TMMAPI-0170). Wire types
 * from `src/api/generated/`.
 * Every call to `run()` must go through the pre-flight dialog (privacy before the call).
 *
 * TMMAPP-0163 (TMMAPI-0193): `run()` starts a background run over every unnamed text and answers
 * `202 {runId}` (a start while one runs answers that run); its status is `currentRun()` /
 * `getRun()`, and `cancelRun()` stops it. `naming-status` adds the split by source (`bySource`).
 */
import { request } from './client';
import { ErrorCodes, isApiError } from './errors';
import type { NamingSource } from './namingSource';
import {
  normalizeRegisterNamedTransfer,
  normalizeRegisterWire,
  normalizeRegisterZelle,
  type RegisterNamedTransfer,
  type RegisterWire,
  type RegisterZelle,
} from './transactions';
import type {
  ForgottenPayeeSuggestion as WireForgotten,
  PageResponseForgottenPayeeSuggestion as WireForgottenPage,
  PageResponsePayeeSuggestionLine as WireLinePage,
  PayeeNamingStatusResponse as WireNamingStatus,
  PayeeSuggestionAcceptAllRequest,
  PayeeSuggestionAcceptAllResponse as WireAcceptAll,
  PayeeSuggestionAcceptRequest,
  PayeeSuggestionAcceptResponse as WireAccept,
  PayeeNamingBySource as WireBySource,
  PayeeNamingRun as WireRun,
  PayeeNamingRunStarted as WireRunStarted,
} from './generated';

/**
 * The owner's texts by how they were named. `UNNAMED` is what `GET /me/payees/unnamed` lists (its
 * `total`), and `KEPT_UNNAMED` the texts kept unnamed (TMMAPI-0195 U3; 0 when not served).
 */
export type PayeeNamingBySource = Record<NamingSource | 'UNNAMED' | 'KEPT_UNNAMED', number>;

export interface PayeeNamingStatus {
  distinctUnresolved: number;
  namedDeterministically: number;
  opaqueRemaining: number;
  aiAvailable: boolean;
  aiUnavailableReason: string | null;
  /** The batches a run sends for everything eligible (`ceil(eligible / 20)`), never a price. */
  estimatedCallCount: number;
  /** The split by source; null when not served. */
  bySource: PayeeNamingBySource | null;
  /**
   * Of `bySource.UNNAMED`, the texts with no standing proposal (TMMAPI-0198 item 7b); 0 when not
   * served, so the card's left-for-you sentence is hidden there, never estimated (TMMAPP-0170).
   */
  unnamedNoSuggestion: number;
}

export type PayeeNamingRunState = 'RUNNING' | 'DONE' | 'STOPPED';

/** A payee-naming run (TMMAPI-0193 U1): `total` is fixed at the start. */
export interface PayeeNamingRun {
  runId: number;
  state: PayeeNamingRunState;
  /** Why a `STOPPED` run stopped, verbatim (CANCELLED, INTERRUPTED, the gate's reason, …). */
  stopReason: string | null;
  total: number;
  evaluated: number;
  proposed: number;
  calls: number;
}

/** One of a suggestion's lines (TMMAPI-0170): what an accept would name, in register terms. */
export interface PayeeSuggestionLine {
  id: number;
  postedOn: string;
  accountId: number;
  accountName: string;
  /** Signed, in the line's bank currency. */
  amountMinor: number;
  currency: string;
  /** The raw bank text. */
  payee: string;
  wire?: RegisterWire;
  zelle?: RegisterZelle;
  transfer?: RegisterNamedTransfer;
}

export interface PayeeSuggestionLinePage {
  items: PayeeSuggestionLine[];
  nextCursor: string | null;
}

export function normalizeSuggestionLinePage(w: WireLinePage): PayeeSuggestionLinePage {
  return {
    items: w.items.map((line) => ({
      id: line.id,
      postedOn: line.postedOn,
      accountId: line.accountId,
      accountName: line.accountName,
      amountMinor: line.amountMinor,
      currency: line.currency,
      payee: line.payee,
      ...(line.wire == null ? {} : { wire: normalizeRegisterWire(line.wire) }),
      ...(line.zelle == null ? {} : { zelle: normalizeRegisterZelle(line.zelle) }),
      ...(line.transfer == null ? {} : { transfer: normalizeRegisterNamedTransfer(line.transfer) }),
    })),
    nextCursor: w.page.nextCursor,
  };
}

/** A forgotten (rejected) suggestion (TMMAPI-0174 §G): what "Bring back" restores. */
export interface ForgottenPayeeSuggestion {
  id: number;
  /** Readable, never a key. */
  displayName: string;
  proposedName: string;
  /** The suggestion's current group: the owner's unresolved lines with its key. */
  lineCount: number;
  /** When it was forgotten (an instant). */
  decidedAt: string | null;
  /** The sample bank line, as imported; null when it is gone. */
  sampleText: string | null;
}

export interface ForgottenPayeeSuggestionPage {
  items: ForgottenPayeeSuggestion[];
  nextCursor: string | null;
}

/** Every field is optional at the pin: an item without an id can't be restored, so it is dropped. */
function normalizeForgotten(w: WireForgotten): ForgottenPayeeSuggestion | null {
  if (w.id === undefined) return null;
  return {
    id: w.id,
    displayName: w.displayName ?? '',
    proposedName: w.proposedName ?? '',
    lineCount: w.lineCount ?? 0,
    decidedAt: w.decidedAt ?? null,
    sampleText: w.sampleText != null && w.sampleText.length > 0 ? w.sampleText : null,
  };
}

export function normalizeForgottenPage(w: WireForgottenPage): ForgottenPayeeSuggestionPage {
  return {
    items: w.items.flatMap((row) => {
      const item = normalizeForgotten(row);
      return item === null ? [] : [item];
    }),
    nextCursor: w.page.nextCursor,
  };
}

export interface PayeeSuggestionAcceptResult {
  suggestionId: number | null;
  payeeId: number | null;
  lines: number | null;
  changed: boolean;
}

export interface PayeeSuggestionAcceptAllResult {
  accepted: number;
  lines: number;
  suggestionIds: number[];
}

export type PayeeSuggestionAcceptBody =
  { payeeId: number } | { name: string } | Record<string, never>;

/** Every field on these responses is optional at the pin: an absent one reads as its default. */
export function normalizeNamingStatus(w: WireNamingStatus): PayeeNamingStatus {
  return {
    distinctUnresolved: w.distinctUnresolved ?? 0,
    namedDeterministically: w.namedDeterministically ?? 0,
    opaqueRemaining: w.opaqueRemaining ?? 0,
    aiAvailable: w.aiAvailable ?? false,
    aiUnavailableReason: w.aiUnavailableReason ?? null,
    estimatedCallCount: w.estimatedCallCount ?? 0,
    bySource: w.bySource == null ? null : normalizeBySource(w.bySource),
    unnamedNoSuggestion: w.unnamedNoSuggestion ?? 0,
  };
}

function normalizeBySource(w: WireBySource): PayeeNamingBySource {
  return {
    RULE: w.RULE ?? 0,
    KNOWN_MERCHANT: w.KNOWN_MERCHANT ?? 0,
    BANK_TEXT: w.BANK_TEXT ?? 0,
    AI: w.AI ?? 0,
    UNNAMED: w.UNNAMED ?? 0,
    KEPT_UNNAMED: w.KEPT_UNNAMED ?? 0,
  };
}

const RUN_STATES: ReadonlySet<string> = new Set(['RUNNING', 'DONE', 'STOPPED']);

/**
 * A run without an id can't be followed, so it is `null`. An unknown state reads as `STOPPED` with
 * its served reason, which the card words neutrally (TMMAPP-0163 item 7).
 */
export function normalizeRun(w: WireRun): PayeeNamingRun | null {
  if (w.runId === undefined) return null;
  const state: PayeeNamingRunState =
    w.state !== undefined && RUN_STATES.has(w.state) ? w.state : 'STOPPED';
  return {
    runId: w.runId,
    state,
    stopReason: w.stopReason != null && w.stopReason.length > 0 ? w.stopReason : null,
    total: w.total ?? 0,
    evaluated: w.evaluated ?? 0,
    proposed: w.proposed ?? 0,
    calls: w.calls ?? 0,
  };
}

/** A status read must name its run; one that doesn't is an error the caller treats as unread. */
function requireRun(w: WireRun): PayeeNamingRun {
  const run = normalizeRun(w);
  if (run === null) throw new Error('payee naming run without an id');
  return run;
}

export function normalizeAcceptResult(w: WireAccept): PayeeSuggestionAcceptResult {
  return {
    suggestionId: w.suggestionId ?? null,
    payeeId: w.payeeId ?? null,
    lines: w.lines ?? null,
    changed: w.changed ?? false,
  };
}

export function normalizeAcceptAllResult(w: WireAcceptAll): PayeeSuggestionAcceptAllResult {
  return {
    accepted: w.accepted ?? 0,
    lines: w.lines ?? 0,
    suggestionIds: w.suggestionIds ?? [],
  };
}

export const payeeSuggestionsApi = {
  /** GET /v1/me/payees/naming-status */
  async namingStatus(): Promise<PayeeNamingStatus> {
    return normalizeNamingStatus(
      await request<WireNamingStatus>('GET', '/v1/me/payees/naming-status'),
    );
  },

  /**
   * POST /v1/me/payees/suggestions/run — the only AI naming call: `202 {runId}`, a background run
   * over every unnamed text (a start while one runs answers that run). Must be invoked only from
   * `AiNamingPreflight` after exclusions are confirmed.
   */
  async run(): Promise<{ runId: number }> {
    const started = await request<WireRunStarted>('POST', '/v1/me/payees/suggestions/run');
    if (started.runId === undefined) throw new Error('payee naming run started without an id');
    return { runId: started.runId };
  },

  /**
   * GET /v1/me/payees/suggestions/runs/current — the run in flight, else the newest; `null` when
   * the person never started one (`404 PAYEE_NAMING_RUN_NOT_FOUND`).
   */
  async currentRun(): Promise<PayeeNamingRun | null> {
    try {
      return normalizeRun(await request<WireRun>('GET', '/v1/me/payees/suggestions/runs/current'));
    } catch (error) {
      if (isApiError(error) && error.code === ErrorCodes.PAYEE_NAMING_RUN_NOT_FOUND) return null;
      throw error;
    }
  },

  /** GET /v1/me/payees/suggestions/runs/{runId} */
  async getRun(runId: number): Promise<PayeeNamingRun> {
    return requireRun(await request<WireRun>('GET', `/v1/me/payees/suggestions/runs/${runId}`));
  },

  /** POST /v1/me/payees/suggestions/runs/{runId}/cancel — idempotent; an ended run answers as it is. */
  async cancelRun(runId: number): Promise<PayeeNamingRun> {
    return requireRun(
      await request<WireRun>('POST', `/v1/me/payees/suggestions/runs/${runId}/cancel`),
    );
  },

  /** POST /v1/me/payees/suggestions/{id}/accept — empty body accepts as proposed. */
  async accept(
    id: number,
    body: PayeeSuggestionAcceptBody = {},
  ): Promise<PayeeSuggestionAcceptResult> {
    const json: PayeeSuggestionAcceptRequest =
      'payeeId' in body && typeof body.payeeId === 'number'
        ? { payeeId: body.payeeId }
        : 'name' in body && typeof body.name === 'string'
          ? { name: body.name }
          : {};
    return normalizeAcceptResult(
      await request<WireAccept>('POST', `/v1/me/payees/suggestions/${id}/accept`, { json }),
    );
  },

  /** POST /v1/me/payees/suggestions/{id}/reject */
  async reject(id: number): Promise<void> {
    await request<void>('POST', `/v1/me/payees/suggestions/${id}/reject`);
  },

  /**
   * POST /v1/me/payees/suggestions/{id}/restore (TMMAPI-0174 §G): a forgotten suggestion is pending
   * again. Idempotent. `409 PAYEE_SUGGESTION_DECIDED` for an accepted one or one that can never be a
   * payee; `404` for an unknown one.
   */
  async restore(id: number): Promise<void> {
    await request<void>('POST', `/v1/me/payees/suggestions/${id}/restore`);
  },

  /**
   * GET /v1/me/payees/suggestions/forgotten?cursor&limit (TMMAPI-0174 §G): the forgotten
   * suggestions, newest decision first, paged by `page.nextCursor`. The page carries no total.
   */
  async forgotten(cursor?: string | null, limit?: number): Promise<ForgottenPayeeSuggestionPage> {
    const query: Record<string, string | number> = {};
    if (cursor != null) query.cursor = cursor;
    if (limit !== undefined) query.limit = limit;
    return normalizeForgottenPage(
      await request<WireForgottenPage>('GET', '/v1/me/payees/suggestions/forgotten', { query }),
    );
  },

  /**
   * GET /v1/me/payees/suggestions/{id}/lines?cursor&limit (TMMAPI-0170): the group's lines, newest
   * first, paged by `page.nextCursor`. Owner-only.
   */
  async lines(
    id: number,
    cursor?: string | null,
    limit?: number,
  ): Promise<PayeeSuggestionLinePage> {
    const query: Record<string, string | number> = {};
    if (cursor != null) query.cursor = cursor;
    if (limit !== undefined) query.limit = limit;
    return normalizeSuggestionLinePage(
      await request<WireLinePage>('GET', `/v1/me/payees/suggestions/${id}/lines`, { query }),
    );
  },

  /** POST /v1/me/payees/suggestions/accept-all */
  async acceptAll(minConfidence: number): Promise<PayeeSuggestionAcceptAllResult> {
    const json: PayeeSuggestionAcceptAllRequest = { minConfidence };
    return normalizeAcceptAllResult(
      await request<WireAcceptAll>('POST', '/v1/me/payees/suggestions/accept-all', { json }),
    );
  },
};
